import apiAuth from './apiAuth';
import { getPerfilByEmail } from './perfil';

import config from '@/config';

const AUTH_API_URL = config.AUTH_API;

/**
 * Realiza login contra el microservicio y guarda token y payload en localStorage y Vuex
 * @param {Object} params - { username, password, app_id, tenant_id }
 * @param {Object} store - instancia de Vuex store
 * @returns {Promise<Object>} payload del usuario si login exitoso, lanza error si falla
 */
export async function login({ username, password, app_id, tenant_id }, store) {
  try {
    const loginData = {
      username,
      password,
      app_id: app_id || config.APP_ID,
      tenant_id: tenant_id || config.TENANT_ID
    };

    const response = await apiAuth.post('/sessions/login', loginData);
    const data = response.data;

    // Guardar app_id y tenant_id en localStorage
    localStorage.setItem('app_id', loginData.app_id);
    localStorage.setItem('tenant_id', loginData.tenant_id);

    // Guardar en localStorage y Vuex
    await store.dispatch('setAuth', {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user_payload: data
    });

    return data;
  } catch (error) {
    console.error('Error en login:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Refresca el access_token usando el refresh_token
 * @param {Object} params - { refresh_token, app_id, tenant_id }
 * @param {Object} store - instancia de Vuex store
 * @returns {Promise<Object>} payload del usuario si refresh exitoso, lanza error si falla
 */
export async function refreshToken({ refresh_token, app_id, tenant_id }, store) {
  try {
    const response = await apiAuth.post('/sessions/refresh', {
      refresh_token,
      app_id: app_id || config.APP_ID,
      tenant_id: tenant_id || config.TENANT_ID
    });
    const data = response.data;
    // Guardar en localStorage y Vuex
    await store.dispatch('setAuth', {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user_payload: data
    });
    return data;
  } catch (error) {
    console.error('Error refrescando token:', error);
    throw error;
  }
}

/**
 * Registra un nuevo usuario
 * @param {Object} params - { username, email, password, tenant_id, roles, app_id }
 * @returns {Promise<Object>} respuesta del backend
 */
export async function signup({ username, email, password, tenant_id, roles, app_id }) {
  try {
    const response = await apiAuth.post('/users/signup', {
      username,
      email,
      password,
      tenant_id: tenant_id || config.TENANT_ID,
      roles,
      app_id: app_id || config.APP_ID
    });
    return response.data;
  } catch (error) {
    console.error('Error en signup:', error);
    throw error;
  }
}

/**
 * Solicita reset de contraseña
 * @param {string} email
 * @returns {Promise<Object>}
 */
export async function requestPasswordReset(email) {
  try {
    const response = await apiAuth.post('/users/request-password-reset', { email });
    return response.data;
  } catch (error) {
    console.error('Error solicitando reset de contraseña:', error);
    throw error;
  }
}

/**
 * Verifica el email usando el token
 * @param {string} token
 * @returns {Promise<Object>}
 */
export async function verifyEmail(token) {
  try {
    const response = await apiAuth.get(`/users/verify-email?token=${encodeURIComponent(token)}`);
    return response.data;
  } catch (error) {
    console.error('Error verificando email:', error);
    throw error;
  }
}

/**
 * Resetea la contraseña usando el token
 * @param {string} token
 * @param {string} new_password
 * @returns {Promise<Object>}
 */
export async function resetPassword(token, new_password) {
  try {
    const response = await apiAuth.post('/users/reset-password', { token, new_password });
    return response.data;
  } catch (error) {
    console.error('Error reseteando contraseña:', error);
    throw error;
  }
}

/**
 * Obtiene el perfil del usuario autenticado
 * @returns {Promise<Object>} perfil del usuario
 */
export async function fetchUserProfile() {
  try {
    const appId = localStorage.getItem('app_id') || config.APP_ID;
    const tenantId = localStorage.getItem('tenant_id') || config.TENANT_ID;



    // Usar GET con query parameters
    const response = await apiAuth.get('/users/me', {
      params: {
        app_id: appId,
        tenant_id: tenantId
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error obteniendo perfil de usuario:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Login y obtención de perfil extendido
 * @param {Object} params - { username, password, app_id, tenant_id }
 * @param {Object} store - instancia de Vuex store
 * @returns {Promise<Object>} { tokens, perfil }
 */
export async function loginConPerfil({ username, password, app_id, tenant_id }, store) {
  try {
    // 1. Realizar login en Auth Services
    const loginData = await login({ username, password, app_id, tenant_id }, store);

    // 2. Obtener perfil del usuario
    const perfil = await fetchUserProfile();

    // 3. Guardar perfil en Vuex/localStorage
    store.commit('set_user_profile', perfil);

    return { tokens: loginData, perfil };
  } catch (error) {
    console.error('Error en loginConPerfil:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Logout del usuario (sesión actual)
 * @param {Object} store - instancia de Vuex store
 * @returns {Promise<boolean>} true si logout exitoso
 */
export async function logout(store) {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    const appId = localStorage.getItem('app_id') || config.APP_ID;
    const tenantId = localStorage.getItem('tenant_id') || config.TENANT_ID;

    if (refreshToken) {
      await apiAuth.post('/sessions/logout', {
        refresh_token: refreshToken,
        app_id: appId,
        tenant_id: tenantId
      });
    }

    // Limpiar datos locales
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('app_id');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('user_payload');

    // Limpiar store
    store.commit('reset_credentials');

    return true;
  } catch (error) {
    console.error('Error en logout:', error.response?.data || error.message);
    // Aunque falle el logout en el servidor, limpiamos datos locales
    localStorage.clear();
    store.commit('reset_credentials');
    return true;
  }
}

/**
 * Logout de todas las sesiones del usuario
 * @param {Object} store - instancia de Vuex store
 * @returns {Promise<boolean>} true si logout exitoso
 */
export async function logoutAll(store) {
  try {
    const appId = localStorage.getItem('app_id') || config.APP_ID;
    const tenantId = localStorage.getItem('tenant_id') || config.TENANT_ID;

    await apiAuth.post('/sessions/logout-all', {
      app_id: appId,
      tenant_id: tenantId
    });

    // Limpiar datos locales
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('app_id');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('user_profile');

    // Limpiar store
    store.commit('reset_credentials');

    return true;
  } catch (error) {
    console.error('Error en logout all:', error.response?.data || error.message);
    // Aunque falle el logout en el servidor, limpiamos datos locales
    localStorage.clear();
    store.commit('reset_credentials');
    return true;
  }
} 